/**
 * useDRBFMWithCAD — fetches a DRBFM session's results and maps failure modes
 * that carry a `cadAnchor` to `CADMarker[]` for overlay on the CAD viewer.
 *
 * Tied to the Toyota Gosei DRBFM↔3D CAD linker PoC (TG #673).
 */
import { useEffect, useMemo, useState } from 'react';
import type { CADMarker } from '../types/cad';
import type { FailureMode, SessionResultsResponse, Severity } from '../types/drbfm';
import { getSessionResults } from '../services/drbfm.service';

interface UseDRBFMWithCADResult {
  failureModes: FailureMode[];
  markers: CADMarker[];
  loading: boolean;
  error: string | null;
}

const severityToType = (s: Severity): CADMarker['type'] => {
  if (s === 'critical' || s === 'high') return 'error';
  if (s === 'medium') return 'warning';
  return 'info';
};

export function useDRBFMWithCAD(sessionId: string | null): UseDRBFMWithCADResult {
  const [results, setResults] = useState<SessionResultsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setResults(null);
      setError(null);
      return;
    }
    let cancelled = false;
    // Clear stale results so an in-flight fetch or a failed load can never
    // surface markers from the previous sessionId. The loading banner takes
    // over from here.
    setResults(null);
    setLoading(true);
    setError(null);
    getSessionResults(sessionId)
      .then((r) => {
        if (!cancelled) setResults(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'failed to load DRBFM results';
          setError(msg);
          setResults(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const failureModes = useMemo(() => results?.failureModes ?? [], [results]);

  const markers = useMemo<CADMarker[]>(
    () =>
      failureModes
        .filter((fm) => fm.cadAnchor)
        .map((fm) => ({
          position: fm.cadAnchor!.position,
          label: fm.failureDescription.slice(0, 40),
          type: severityToType(fm.severity),
          failureModeId: fm.id,
          confidence: fm.cadAnchor!.confidence,
          detailPayload: fm,
        })),
    [failureModes],
  );

  return { failureModes, markers, loading, error };
}
